import React, { useState, useRef } from "react";
import { FileText, Brain, Sparkles, Copy, Search, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { governingLawOptions } from "../templates/templateData";
import { apiClient } from "../../App";
import { toast } from "sonner";

export function WizardStepTemplate({ form, setForm, templates, hasFeature, onSelectTemplate, onStepChange }) {
  const [aiScope, setAiScope] = useState("");
  const [aiJurisdiction, setAiJurisdiction] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiProgress, setAiProgress] = useState("");
  const [aiScratchOpen, setAiScratchOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneQuery, setCloneQuery] = useState("");
  const [cloneResults, setCloneResults] = useState([]);
  const [cloneSearching, setCloneSearching] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const cloneTimeoutRef = useRef(null);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleAiGenerate = async () => {
    if (!aiScope.trim() || aiScope.trim().length < 20) {
      toast.error("Please describe what the contract is for (at least a few sentences).");
      return;
    }
    setAiGenerating(true);
    setAiProgress("Starting AI generation...");
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      if (elapsed < 15) setAiProgress("Analysing contract scope...");
      else if (elapsed < 30) setAiProgress("Drafting contract sections...");
      else if (elapsed < 60) setAiProgress("Generating legal clauses and terms...");
      else setAiProgress(`Still working... (${elapsed}s)`);
    }, 3000);
    try {
      const res = await apiClient.post("/ai/generate-template", {
        description: aiScope, jurisdiction: aiJurisdiction, contract_type_hint: "",
      });
      const taskId = res.data.task_id;
      pollRef.current = setInterval(async () => {
        try {
          const poll = await apiClient.get(`/ai/generate-template/${taskId}`);
          if (poll.data.status === "completed" && poll.data.result) {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            const { suggested_title, suggested_type, sections } = poll.data.result;
            const content = sections.map(s => `## ${s.name}\n\n${s.content}`).join("\n\n---\n\n");
            setForm(prev => ({
              ...prev, title: suggested_title || prev.title, contract_type: suggested_type || prev.contract_type,
              contract_scope: aiScope, sections, content, template_id: null, template_metadata: null,
            }));
            toast.success(`AI generated ${sections.length} sections`);
            setAiGenerating(false);
            setAiProgress("");
            onStepChange(1);
          } else if (poll.data.status === "failed") {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            toast.error(poll.data.error || "Generation failed.");
            setAiGenerating(false);
            setAiProgress("");
          }
        } catch {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          toast.error("Lost connection to AI task");
          setAiGenerating(false);
          setAiProgress("");
        }
      }, 4000);
    } catch (err) {
      clearInterval(timerRef.current);
      toast.error(err.response?.data?.detail || "AI generation failed.");
      setAiGenerating(false);
      setAiProgress("");
    }
  };

  const searchContracts = (query) => {
    setCloneQuery(query);
    if (cloneTimeoutRef.current) clearTimeout(cloneTimeoutRef.current);
    if (query.length < 2) { setCloneResults([]); return; }
    cloneTimeoutRef.current = setTimeout(async () => {
      setCloneSearching(true);
      try {
        const res = await apiClient.get(`/contract-management/search?q=${encodeURIComponent(query)}`);
        setCloneResults((res.data || []).slice(0, 8));
      } catch { setCloneResults([]); }
      finally { setCloneSearching(false); }
    }, 300);
  };

  const cloneContract = async (contractId, contractTitle) => {
    try {
      const res = await apiClient.get(`/contracts/${contractId}`);
      const src = res.data;
      const cf = src.custom_fields || {};
      setForm(prev => ({
        ...prev, title: `${src.title} (Copy)`, contract_type: src.contract_type || "",
        contract_value: src.contract_value || "", currency: src.currency || "",
        effective_date: "", expiration_date: "", department: src.department || "",
        spend_category: src.spend_category || "", content: src.content || "",
        contracting_party: { legal_name: "", registered_address: "", contact_email: "", contact_phone: "" },
        sections: (src.sections || []).map(s => ({ ...s })),
        deliverables: (cf.deliverables || []).map(d => ({ description: d.description || "", delivery_date: "", recurrence: d.recurrence || "once" })),
        kpis: (cf.kpis || []).map(k => ({ description: k.description || "", measure: k.measure || "" })),
        template_id: cf.template_id || null, template_metadata: cf.template_metadata || null,
      }));
      setCloneOpen(false);
      setCloneQuery("");
      setCloneResults([]);
      toast.success(`Cloned from "${contractTitle}" — update supplier and dates`);
      onStepChange(1);
    } catch { toast.error("Failed to load contract for cloning"); }
  };

  return (
    <div className="space-y-4" data-testid="wizard-step-template-content">
      <div>
        <h2 className="text-lg font-medium">Choose a Template</h2>
        <p className="text-sm text-slate-500 mt-1">Start from a pre-built template or let AI generate one from your requirements.</p>
      </div>

      {/* AI Generate */}
      {hasFeature("ai_template_generation") && (
        <div className={`border rounded-sm transition-all ${aiScratchOpen ? "border-violet-300 bg-violet-50/20" : ""}`}>
          <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-violet-50/30 transition-colors rounded-sm" onClick={() => setAiScratchOpen(!aiScratchOpen)} data-testid="start-from-scratch">
            <div className="w-9 h-9 rounded bg-gradient-to-br from-violet-100 to-violet-200 flex items-center justify-center shrink-0"><Brain size={18} className="text-violet-600" /></div>
            <div className="flex-1"><p className="font-medium text-sm">AI-Powered Draft</p><p className="text-xs text-slate-400">Describe what the contract is for — AI generates a complete professional template</p></div>
            <Badge className="bg-violet-100 text-violet-700 border-violet-200 rounded-sm text-[10px]">AI</Badge>
          </button>
          {aiScratchOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-violet-100" data-testid="ai-generate-form">
              <div className="space-y-1 mt-3">
                <Label className="text-xs font-medium">Describe what this contract is for *</Label>
                <Textarea value={aiScope} onChange={e => setAiScope(e.target.value)} placeholder="e.g., We need a contract for outsourced IT helpdesk support..." rows={5} className="rounded-sm text-sm" data-testid="ai-scope-input" />
                <p className="text-[10px] text-slate-400">The more detail you provide, the better the AI-generated template.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Governing Jurisdiction (optional)</Label>
                <Select value={aiJurisdiction} onValueChange={setAiJurisdiction}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="ai-jurisdiction-select"><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                  <SelectContent className="max-h-60">{governingLawOptions.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={handleAiGenerate} disabled={aiGenerating || aiScope.trim().length < 20} className="w-full gap-2 rounded-sm bg-violet-600 hover:bg-violet-700" data-testid="ai-generate-btn">
                {aiGenerating ? <><Loader2 size={15} className="animate-spin" />Generating...</> : <><Sparkles size={15} />Generate Contract Template</>}
              </Button>
              {aiGenerating && (
                <div className="text-center space-y-1">
                  <p className="text-xs text-violet-600 dark:text-violet-400 font-medium animate-pulse" data-testid="ai-progress-text">{aiProgress}</p>
                  <p className="text-[10px] text-slate-400">AI generates 6-12 contract sections. This usually takes 30-60 seconds.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Clone from Existing */}
      <div className={`border rounded-sm transition-all ${cloneOpen ? "border-cyan-300 bg-cyan-50/20" : ""}`}>
        <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-cyan-50/30 transition-colors rounded-sm" onClick={() => setCloneOpen(!cloneOpen)} data-testid="clone-from-existing">
          <div className="w-9 h-9 rounded bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center shrink-0"><Copy size={18} className="text-cyan-600" /></div>
          <div className="flex-1"><p className="font-medium text-sm">Clone from Existing</p><p className="text-xs text-slate-400">Duplicate a previous contract — keeps clauses, deliverables and KPIs</p></div>
        </button>
        {cloneOpen && (
          <div className="px-4 pb-4 space-y-3 border-t border-cyan-100" data-testid="clone-search-form">
            <div className="space-y-1 mt-3">
              <Label className="text-xs font-medium">Search contracts to clone</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={cloneQuery} onChange={e => searchContracts(e.target.value)} placeholder="Search by title, supplier, type..." className="rounded-sm pl-9 text-sm" data-testid="clone-search-input" />
                {cloneSearching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
              </div>
            </div>
            {cloneResults.length > 0 && (
              <div className="border rounded-sm max-h-56 overflow-y-auto divide-y" data-testid="clone-results">
                {cloneResults.map(c => (
                  <button key={c.id} className="w-full flex items-start gap-3 p-2.5 text-left hover:bg-cyan-50/40 transition-colors" onClick={() => cloneContract(c.id, c.title)} data-testid={`clone-option-${c.id}`}>
                    <FileText size={15} className="text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.title}</p>
                      <p className="text-[10px] text-slate-400 truncate">{c.contracting_party?.legal_name || "No supplier"}{c.contract_type ? ` · ${c.contract_type}` : ""}{c.contract_value ? ` · ${c.currency || ""} ${Number(c.contract_value).toLocaleString()}` : ""}</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px] shrink-0">{c.status}</Badge>
                  </button>
                ))}
              </div>
            )}
            {cloneQuery.length >= 2 && !cloneSearching && cloneResults.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No contracts found for "{cloneQuery}"</p>}
            <p className="text-[10px] text-slate-400">Supplier details and dates are cleared — you'll fill those in the next step.</p>
          </div>
        )}
      </div>

      {/* Template List */}
      {templates.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Available Templates</p>
          <div className="grid gap-2">
            {templates.map(tmpl => (
              <button key={tmpl.id} className={`w-full flex items-start gap-3 p-3 border rounded-sm text-left transition-colors hover:border-cyan-300 hover:bg-cyan-50/30 ${form.template_id === tmpl.id ? "border-cyan-400 bg-cyan-50/50" : ""}`} onClick={() => onSelectTemplate(tmpl)} data-testid={`wizard-template-${tmpl.id}`}>
                <div className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center shrink-0 mt-0.5"><FileText size={16} className="text-slate-500" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{tmpl.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{tmpl.description || tmpl.category}</p>
                  {tmpl.sections?.length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {tmpl.sections.slice(0, 3).map((s, idx) => <Badge key={s.name || idx} variant="secondary" className="text-[10px]">{s.name}</Badge>)}
                      {tmpl.sections.length > 3 && <Badge variant="secondary" className="text-[10px]">+{tmpl.sections.length - 3}</Badge>}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
